<template>
  <div class="publisher py-2 px-3">
    <img v-if="avatar" class="avatar avatar-xs rounded" :src="avatar" />

    <span
      ref="editable"
      class="editable"
      :contenteditable="true"
      @input="adjustTextareaHeight"
      @keydown.enter="sendMessageOnEnter"
      @mousedown="fieldInteraction"
      @focus="fieldInteraction"
      @blur="fieldInteraction"
    >
      {{ message || placeholderText }}
    </span>

    <i class="fa fa-paperclip browse" @click="$emit('file-browser', room.id)"></i>
    <i class="fa fa-paper-plane send-message" @click="sendChatMessage(room.id)"></i>
  </div>
</template>

<script>
import store from "../../../store";
import { broadcastMessage, sendMessage } from "../../../services/api/uni-chat";
import { eventBus } from "../../../utils/helpers";

export default {
  name: "ConversationInput",
  props: {
    avatar: {
      type: String,
      default: ''
    },
    room: {
      type: [Array, Object],
      default: []
    },
    user: {
      type: [Array, Object],
      default: []
    }
  },
  data() {
    return {
      message: '',
      textareaRows: 1,
      isTyping: false,
      isSendingMessage: false,
      placeholderText: ''
    }
  },
  mounted() {
    // Display placeholder text initially
    this.$refs.editable.innerText = this.placeholderText;
    this.adjustTextareaHeight();
  },
  methods: {
    adjustTextareaHeight() {
      const editable = this.$refs.editable;
      const text = editable.innerText.trim();

      // Calculate height based on content
      this.textareaRows = Math.max(Math.ceil(editable.scrollHeight / 24), 1);

      // Update message based on actual content
      const html = editable.innerHTML.replace(/<br\s*[/]?>/gi, "\n");
      this.message = text || html.trim();

      // If user deletes all the text and the input never had focus, restore the placeholder
      if (!text && !this.isTyping && this.message !== this.placeholderText) {
        editable.innerText = this.placeholderText;
      }
    },
    fieldInteraction(event) {
      const editable = this.$refs.editable;
      const text = editable.innerText.trim();

      if (event.type === "mousedown" || event.type === "focus") {
        // Clear placeholder text when span is clicked or focused
        if (text === this.placeholderText) {
          editable.innerText = "";
        }

        this.isTyping = true;
      } else if (event.type === "blur") {
        if (!text) {
          editable.innerText = this.placeholderText;
        }
      }
    },
    async sendChatMessage(room_id) {
      this.$refs.editable.innerText = '';
      if (!this.message.trim()) {
        return;
      }

      try {
        this.isSendingMessage = true;
        const response = await sendMessage(room_id, {
          message: this.message
        });

        if (response.status === 200) {
          this.message = '';
          this.textareaRows = 1;

          const channel = new BroadcastChannel('chat');
          channel.postMessage({
            type: 'sending-message',
            room_id: room_id,
            message: response.data.message
          });

          // Broadcast newly sent message based on new Message ID
          await broadcastMessage(room_id, { message_id: response.data.message.id });
        } else {
          console.error('Unexpected status:', response.status);
        }
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        this.isSendingMessage = false;
      }
    },
    sendMessageOnEnter(event) {
      this.$refs.editable.innerText = '';

      if (event.key === 'Enter' && !event.shiftKey) {
        if (!this.isSendingMessage) {
          this.sendChatMessage(this.room.id);
        }
        event.preventDefault(); // Prevent default behavior (new line)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.publisher {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 5px 10px;
  background-color: #f9fafb;
  z-index: 1;

  .editable {
    color: slategray;
    flex: 1;
    display: block;
    width: calc(100% - 50px);
    padding: 2px 4px;
    border: none;
    border-radius: 5px;
    margin: 0;
    min-height: 1rem;
    max-height: 120px;
    overflow-y: auto;
    white-space: pre-wrap;
    outline: none;
  }

  .browse {
    color: #cac7c7;
    cursor: pointer;
    margin-right: 0;

    &:hover {
      color: slategray;
    }
  }

  .send-message {
    color: #467fd0;
    cursor: pointer;
    margin-right: 0;

    &:hover {
      color: #3668ae;
    }
  }
}
</style>
